import type { Client } from "@connectrpc/connect";
import type { HubService } from "./gen/hub_pb";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Invoke a clip command via Hub and return the parsed result.
 *
 * The Hub's Invoke RPC is server-streaming: each response carries a chunk of output.
 * Each chunk is parsed independently. Returns a single value for one chunk,
 * or an array for multiple chunks.
 */
export async function hubInvoke(
  client: Client<typeof HubService>,
  clipName: string,
  command: string,
  input: unknown,
  clipToken?: string,
): Promise<unknown> {
  const inputBytes = encoder.encode(JSON.stringify(input));
  const results: unknown[] = [];

  for await (const response of client.invoke({
    clipName,
    command,
    input: inputBytes,
    clipToken: clipToken ?? "",
  })) {
    if (response.error) {
      throw new Error(
        response.error.message || response.error.code || "Hub invoke error",
      );
    }

    if (response.output.length > 0) {
      const text = decoder.decode(response.output);
      try {
        results.push(JSON.parse(text));
      } catch {
        results.push(text);
      }
    }
  }

  if (results.length === 0) return undefined;
  if (results.length === 1) return results[0];
  return results;
}
